import React from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiGet } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

// Helper to format point value with +/-
function formatPoints(val) {
  if (val === undefined || val === null) return '0';
  return val >= 0 ? `+${val}` : `${val}`;
}

// Helper to get CSS class for point value
function getPointsClass(val) {
  if (val === undefined || val === null) return 'text-gray-400';
  if (val > 0) return 'text-green-400';
  if (val < 0) return 'text-red-400';
  return 'text-gray-400';
}

export default function Scoring({ t, slug }) {
  const { data: pointsConfig, isLoading, isError } = useQuery({
    queryKey: ['points', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/points`),
    enabled: !!slug,
    placeholderData: keepPreviousData,
  });

  // Default values if not loaded yet
  const p = pointsConfig || {};

  const SCORING_SECTIONS = [
    {
      icon: '⚔️', titleKey: 'scoring_cw_title',
      subsections: [
        { nameKey: 'scoring_attack_3_star', icon: '🗡️', rules: [
          { condKey: 'scoring_higher_th', points: formatPoints(p.points_cw_3_stars_up) },
          { condKey: 'scoring_same_th', points: formatPoints(p.points_cw_3_stars_same) },
          { condKey: 'scoring_lower_th', points: formatPoints(p.points_cw_3_stars_down) },
        ]},
        { nameKey: 'scoring_attack_2_star', icon: '🗡️', rules: [
          { condKey: 'scoring_higher_th', points: formatPoints(p.points_cw_2_stars_up) },
          { condKey: 'scoring_same_th', points: formatPoints(p.points_cw_2_stars_same) },
          { condKey: 'scoring_lower_th', points: formatPoints(p.points_cw_2_stars_down) },
        ]},
        { nameKey: 'scoring_attack_1_star', icon: '🗡️', rules: [
          { condKey: 'scoring_higher_th', points: formatPoints(p.points_cw_1_star_up) },
          { condKey: 'scoring_same_th', points: formatPoints(p.points_cw_1_star_same) },
          { condKey: 'scoring_lower_th', points: formatPoints(p.points_cw_1_star_down) },
        ]},
        { nameKey: 'scoring_attack_0_star', icon: '🗡️', rules: [{ condKey: 'scoring_any_th', points: formatPoints(p.points_cw_0_stars) }] },
        { nameKey: 'scoring_missed_attack', rules: [{ condKey: 'scoring_per_missed', points: formatPoints(p.points_cw_missed_attack_penalty) }] },
        { nameKey: 'scoring_defense', icon: '🛡️', rules: [
          { condKey: 'scoring_stars_3_received', points: formatPoints(p.points_defense_3_stars) },
          { condKey: 'scoring_stars_2_received', points: formatPoints(p.points_defense_2_stars) },
          { condKey: 'scoring_stars_1_received', points: formatPoints(p.points_defense_1_star) },
          { condKey: 'scoring_stars_0_received', points: formatPoints(p.points_defense_0_stars) },
        ]},
      ],
    },
    {
      icon: '🏆', titleKey: 'scoring_cwl_title',
      subsections: [
        { nameKey: 'scoring_cwl_attack_stars', icon: '🗡️', rules: [
          { condKey: 'scoring_cwl_3_stars', points: formatPoints(p.points_cwl_3_stars) },
          { condKey: 'scoring_cwl_2_stars', points: formatPoints(p.points_cwl_2_stars) },
          { condKey: 'scoring_cwl_1_star', points: formatPoints(p.points_cwl_1_star) },
          { condKey: 'scoring_cwl_0_stars', points: formatPoints(p.points_cwl_0_stars) },
        ]},
        { nameKey: 'scoring_missed_attack', rules: [{ condKey: 'scoring_per_missed', points: formatPoints(p.points_cwl_missed_attack_penalty) }] },
        { nameKey: 'scoring_defense', icon: '🛡️', rules: [
          { condKey: 'scoring_stars_3_received', points: formatPoints(p.points_defense_3_stars) },
          { condKey: 'scoring_stars_2_received', points: formatPoints(p.points_defense_2_stars) },
          { condKey: 'scoring_stars_1_received', points: formatPoints(p.points_defense_1_star) },
          { condKey: 'scoring_stars_0_received', points: formatPoints(p.points_defense_0_stars) },
        ]},
      ],
    },
    {
      icon: '➕', titleKey: 'scoring_extra_title',
      subsections: [
        { nameKey: 'scoring_donations_name', icon: '💝', rules: [{ condKey: 'scoring_donations_rule', donations: p.points_donation_milestone || 1000 }] },
        { nameKey: 'scoring_discord_name', icon: '🎮', descKey: 'scoring_discord_desc', rules: [{ condKey: 'scoring_discord_rule', points: formatPoints(p.points_discord_member_bonus) }] },
      ],
    },
  ];

  const SECTION_ACCENTS = ['gold', 'purple', 'teal'];

  if (isLoading) {
    return (
      <div id="infoPage">
        <h1 className="text-2xl md:text-3xl mb-6">📊 <span>{t('page_scoring')}</span></h1>
        <div className="bg-gray-800 rounded-xl">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div id="infoPage">
        <h1 className="text-2xl md:text-3xl mb-6">📊 <span>{t('page_scoring')}</span></h1>
        <div className="bg-gray-800 p-6 rounded-xl text-center text-red-400">{t('error_loading_data')}</div>
      </div>
    );
  }

  return (
    <div id="infoPage">
      <h1 className="text-2xl md:text-3xl mb-6">📊 <span>{t('page_scoring')}</span></h1>

      <div id="scoringContainer" className="sc-container">
        {SCORING_SECTIONS.map((section, si) => {
          const accent = SECTION_ACCENTS[si % SECTION_ACCENTS.length];
          return (
            <div key={si} className={`sc-section sc-section-${accent}`}>
              <h2 className="sc-section-title">{section.icon} {t(section.titleKey)}</h2>
              <div className="sc-subsections">
                {section.subsections.map((sub, subi) => (
                  <div key={subi} className="sc-sub">
                    <h3 className="sc-sub-title">{sub.icon ? `${sub.icon} ` : ''}{t(sub.nameKey)}</h3>
                    {sub.descKey && <p className="sc-sub-desc">{t(sub.descKey)}</p>}
                    {sub.rules.length > 0 && (
                      <div className="sc-rules-list">
                        {sub.rules.map((rule, ri) => (
                          <div key={ri} className="sc-rule-row">
                            <span className="sc-rule-label">{t(rule.condKey, { donations: rule.donations })}</span>
                            <span className={`sc-pts-pill${parseInt(rule.points) > 0 ? ' sc-pts-pos' : parseInt(rule.points) < 0 ? ' sc-pts-neg' : ' sc-pts-zero'}`}>
                              {rule.points}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        <div className="sc-footer">ℹ️ {t('scoring_footer')}</div>
      </div>
    </div>
  );
}
